import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getClients, getShifts } from '../utils/storageUtils';
import { format, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { generateTodaysShifts } from '../utils/autoShiftGenerator';

function Dashboard() {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    shiftsThisWeek: 0,
    hoursThisWeek: 0,
    recentShifts: []
  });
  const autoShiftRan = useRef(false);

  useEffect(() => {
    // Auto-generate shifts from recurring schedules (once per session)
    if (!autoShiftRan.current) {
      autoShiftRan.current = true;
      try {
        const created = generateTodaysShifts();
        if (created > 0) {
          console.log(`Auto-generated ${created} shift(s) from roster`);
        }
      } catch (err) {
        console.error('Auto-shift generation error:', err);
      }
    }
    loadStats();
  }, []);

  const loadStats = () => {
    const clients = getClients();
    const allShifts = getShifts();
    
    // Calculate this week's date range
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    
    // Filter shifts for this week
    const shiftsThisWeek = allShifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      return isWithinInterval(shiftDate, { start: weekStart, end: weekEnd });
    });
    
    // Calculate total hours this week
    const hoursThisWeek = shiftsThisWeek.reduce((sum, shift) => sum + (shift.hours || 0), 0);
    
    // Get active clients (clients with shifts in last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const activeClientIds = new Set(
      allShifts
        .filter(shift => new Date(shift.date) >= thirtyDaysAgo)
        .map(shift => shift.clientId)
    );
    
    // Get recent shifts (last 5)
    const recentShifts = allShifts
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5)
      .map(shift => {
        const client = clients.find(c => c.id === shift.clientId);
        return {
          ...shift,
          clientName: client ? client.name : 'Unknown Client'
        };
      });
    
    setStats({
      totalClients: clients.length,
      activeClients: activeClientIds.size,
      shiftsThisWeek: shiftsThisWeek.length,
      hoursThisWeek: hoursThisWeek.toFixed(1),
      recentShifts
    });
  };

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Clients</h3>
          <div className="stat-value">{stats.totalClients}</div>
          <Link to="/clients" className="stat-link">Manage Clients →</Link>
        </div>
        
        <div className="stat-card">
          <h3>Active Clients</h3>
          <div className="stat-value">{stats.activeClients}</div>
          <p className="stat-subtitle">Last 30 days</p>
        </div>
        
        <div className="stat-card">
          <h3>Shifts This Week</h3>
          <div className="stat-value">{stats.shiftsThisWeek}</div>
          <Link to="/shifts" className="stat-link">View Shifts →</Link>
        </div>
        
        <div className="stat-card">
          <h3>Hours This Week</h3>
          <div className="stat-value">{stats.hoursThisWeek}</div>
          <p className="stat-subtitle">Total hours worked</p>
        </div>
      </div>

      <div className="dashboard-section">
        <h2>Recent Shifts</h2>
        {stats.recentShifts.length === 0 ? (
          <div className="empty-state">
            <p>No shifts recorded yet.</p>
            <Link to="/shifts" className="btn btn-primary">Add Your First Shift</Link>
          </div>
        ) : (
          <div className="recent-shifts-list">
            {stats.recentShifts.map(shift => (
              <div key={shift.id} className="shift-item">
                <div className="shift-date">
                  {format(new Date(shift.date), 'EEE, MMM d, yyyy')}
                </div>
                <div className="shift-client">{shift.clientName}</div>
                <div className="shift-details">
                  {shift.hours}h • {shift.serviceDescription}
                </div>
                <div className="shift-amount">${shift.total?.toFixed(2) || '0.00'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <Link to="/shifts" className="btn btn-primary">Add New Shift</Link>
          <Link to="/invoices" className="btn btn-secondary">Generate Invoice</Link>
          <Link to="/clients" className="btn btn-secondary">Add Client</Link>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
